/* =========================================
   SHARED SEO CONSTANTS
   Single source of truth for canonical URL, brand naming,
   and structured data. Keep brand strings consistent —
   Google builds entity recognition from repetition.
   ========================================= */

export const SITE_URL = 'https://payna.com'

export const BRAND = 'Payna'
export const LEGAL_NAME = 'Paena Technologies, Inc.'

/* Terms the licensing tables are published under. Google's Dataset
   docs want a URL that pins a specific version of a licence, so this
   points at the versioned CC deed rather than our terms page, which
   would move underneath the link. Reuse with attribution is the point:
   it is the same bet as llms.txt. */
export const DATA_LICENSE = 'https://creativecommons.org/licenses/by/4.0/'

export const SITE_NAME = 'Payna'

/* The homepage title is the one place the brand has to introduce
   itself. "Payna" alone competes with an unrelated Wikipedia entity,
   a game character, and a food brand for the bare word, and tells a
   searcher who does find it nothing about what this is. Keep the
   brand first (it is what people who already know us type) and the
   category immediately after. */
export const DEFAULT_TITLE =
  'Payna | AI Licensing and Compliance for Regulated Companies'

/* 318 characters until 2026-08-27, which is roughly double what a
   SERP will render, so most of it was never seen. It also read as a
   keyword inventory rather than a reason to click.

   What it says now is the thing competitors structurally do not do:
   license the COMPANY and the PEOPLE, on both registries. Brico
   covers entity licensing and states plainly that its mortgage
   product excludes individual MLO licensing; the insurance-native
   platforms do people but not the company. Both halves is the
   position, so both halves lead. */
export const DEFAULT_DESCRIPTION =
  'Payna licenses the company and the people who work for it. ' +
  'Mortgage on NMLS, insurance on NIPR, applications through ' +
  'renewals, in all 50 states.'

/* Keywords are largely ignored by Google but still parsed by Bing,
   DuckDuckGo, and a number of AI crawlers. Ordered to match the site's
   positioning: brand, then mortgage and insurance, then lending and
   collections, then the payments verticals. */
export const KEYWORDS = [
  'Payna',
  'Payna compliance',
  'Payna licensing',
  'Paena Technologies',
  'mortgage licensing',
  'MLO licensing',
  'MU4 filing',
  'NMLS renewal automation',
  'NMLS sponsorship management',
  'mortgage company license',
  'insurance producer license',
  'insurance adjuster license',
  'NIPR licensing',
  'adjuster licensing software',
  'producer appointment management',
  'CE tracking',
  'lending license',
  'debt collection license',
  'collection agency license',
  'multi-state licensing',
  'state licensing compliance',
  'license renewal tracking',
  'surety bond compliance',
  'AI licensing agent',
  'licensing automation',
  'compliance automation software',
  'regulatory compliance platform',
  'license status API',
  'money transmitter license',
  'MTL licensing',
  'fintech licensing',
  'BSA AML compliance',
  'compliance operating system',
]

/* External profiles that corroborate the brand entity.
   VERIFY each URL resolves before shipping — a broken sameAs
   weakens the entity signal rather than strengthening it. */
export const SAME_AS = [
  /* Wikidata first. It is the only entry here that is an IDENTIFIER
     rather than a profile: Q141187417 is the node Google's Knowledge
     Graph reconciles against, and the item's own P856 points back at
     payna.com, so the claim is mutual and machine-checkable in both
     directions. That round trip is what promotes "payna" from a string
     Google spell-corrects to "patna" into a thing it recognises. */
  'https://www.wikidata.org/wiki/Q141187417',
  'https://www.ycombinator.com/companies/payna',
  'https://www.linkedin.com/company/payna',
  'https://x.com/payna_co',
  'https://www.crunchbase.com/organization/payna',
]
/* Checked 2026-08-25: the first three return 200 to an anonymous
   fetch. Crunchbase answers 403 to every non-browser client, so it
   cannot be checked that way; it ships on the owner's confirmation
   that the profile exists. A Cloudflare 403 is bot filtering, not a
   missing page, and Google crawls Crunchbase normally. PitchBook
   stays out until someone confirms that profile the same way.

   Wikidata Q141187417 created and verified 2026-08-27: label "Payna",
   description "American regulatory technology company", aliases Paena
   Technologies / Payna AI / Paena Technologies, Inc., and ten
   statements each carrying a reference. */

/* Complete per-page metadata in one call. Without this, subpages
   that set only title/description inherit the LAYOUT's openGraph
   and twitter blocks, so shared links preview as the homepage.
   A page-level openGraph REPLACES the root's resolved one
   (including the file-convention og:image), so the image must be
   restated here explicitly. */
const OG_IMAGE = {
  url: '/opengraph-image.png',
  width: 1200,
  height: 630,
  type: 'image/png',
  alt:
    'Payna: From the first license to the hundredth. One system for every ' +
    'application, renewal, and seat.',
}

export function pageMetadata({
  title,
  description,
  path,
}: {
  title: string
  description: string
  path: string
}) {
  const fullTitle = `${title} | ${SITE_NAME}`
  return {
    title,
    description,
    alternates: { canonical: path },
    openGraph: {
      type: 'website' as const,
      siteName: SITE_NAME,
      locale: 'en_US',
      title: fullTitle,
      description,
      url: path,
      images: [OG_IMAGE],
    },
    twitter: {
      card: 'summary_large_image' as const,
      title: fullTitle,
      description,
      images: [OG_IMAGE.url],
    },
  }
}

/* Same contract as pageMetadata, but emits og:type=article plus the
   published/modified timestamps. Separate from pageMetadata rather
   than an extra flag on it so the common case stays a three-key
   call, and so a product page can never accidentally claim to be an
   article. `noindex` exists for drafts, which are reachable by URL
   in development only. */
export function articleMetadata({
  title,
  description,
  path,
  publishedTime,
  modifiedTime,
  noindex = false,
}: {
  title: string
  description: string
  path: string
  publishedTime: string
  modifiedTime?: string
  noindex?: boolean
}) {
  const fullTitle = `${title} | ${SITE_NAME}`
  return {
    title,
    description,
    alternates: { canonical: path },
    ...(noindex ? { robots: { index: false, follow: false } } : {}),
    openGraph: {
      type: 'article' as const,
      siteName: SITE_NAME,
      locale: 'en_US',
      title: fullTitle,
      description,
      url: path,
      images: [OG_IMAGE],
      publishedTime,
      modifiedTime: modifiedTime ?? publishedTime,
      authors: [SITE_URL],
    },
    twitter: {
      card: 'summary_large_image' as const,
      title: fullTitle,
      description,
      images: [OG_IMAGE.url],
    },
  }
}
